import { useEffect, useState } from 'react';
import { Users, UserPlus, Shield, IndianRupee, AlertCircle, Clock, FileCheck } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import StatCard from '../components/StatCard';
import LoadingSpinner from '../components/LoadingSpinner';
import EmployeeDashboard from './EmployeeDashboard';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../services/api';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
}

function HrDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [premiumData, setPremiumData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      dashboardAPI.metrics(),
      dashboardAPI.premiumSummary().catch(() => ({ data: [] })),
    ])
      .then(([metricsRes, premiumRes]) => {
        setMetrics(metricsRes.data);
        const chartData = (premiumRes.data || []).slice(0, 8).map((e) => ({
          name: e.full_name?.split(' ')[0] || e.employee_code,
          premium: parseFloat(e.total_premium) || 0,
        }));
        setPremiumData(chartData);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">HR Dashboard</h1>
        <p className="text-slate-500 mt-1">Real-time coverage and premium analytics</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard title="Employees Covered" value={metrics?.totalEmployeesCovered || 0} icon={Users} color="primary" />
        <StatCard title="Dependents Covered" value={metrics?.totalDependentsCovered || 0} icon={UserPlus} color="green" />
        <StatCard title="Active Policies" value={metrics?.activePolicies || 0} icon={Shield} color="purple" />
        <StatCard
          title="Total Premium"
          value={formatCurrency(metrics?.totalAnnualPremium)}
          icon={IndianRupee}
          color="amber"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          title="Claims Ratio"
          value={`${metrics?.claimsRatio || 0}%`}
          subtitle="Settled vs Premium"
          icon={FileCheck}
          color="green"
        />
        <StatCard title="Pending Claims" value={metrics?.pendingClaims || 0} icon={AlertCircle} color="red" />
        <StatCard title="Renewal Due (60 days)" value={metrics?.renewalDueList || 0} icon={Clock} color="amber" />
        <StatCard title="Pending Endorsements" value={metrics?.endorsementsPending || 0} icon={FileCheck} color="primary" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard
          title="Premium Collected"
          value={formatCurrency(metrics?.premiumCollected)}
          subtitle="Payroll deductions recorded"
          icon={IndianRupee}
          color="green"
        />
        <StatCard
          title="Premium Due"
          value={formatCurrency(metrics?.premiumDue)}
          subtitle="Outstanding reconciliation"
          icon={IndianRupee}
          color="amber"
        />
      </div>

      {premiumData.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Premium by Employee</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={premiumData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="premium" fill="#2563eb" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  if (user?.role === 'employee') return <EmployeeDashboard />;
  return <HrDashboard />;
}
